// Universal Force Field (UFF) Lennard-Jones parameters, per element.
//
// Source: Rappé, Casewit, Colwell, Goddard III, Skiff, "UFF, a Full Periodic
// Table Force Field for Molecular Mechanics and Molecular Dynamics
// Simulations", J. Am. Chem. Soc. 1992, 114, 10024–10035 — Table 1, the
// nonbond columns x1 (distance) and D1 (energy). UFF is the natural choice
// here because it parameterises the *entire* periodic table from a single
// scheme, so a sandbox spanning H…Lr can be translated uniformly.
//
// IMPORTANT unit/convention notes (these are easy to get wrong):
//
//  • UFF's x1 is the LJ potential *minimum* distance R_min (the separation of
//    lowest energy), NOT the zero-crossing σ that LAMMPS `pair_style lj/cut`
//    expects. They differ by 2^(1/6):  σ = x1 · 2^(−1/6).
//    We pre-apply that factor below, so `sigma` here is already LAMMPS-ready.
//
//  • UFF energies (D1) are in kcal/mol and distances in Å. That is exactly
//    LAMMPS `units real` (energy kcal/mol, distance Å), so the LAMMPS writer
//    uses `units real` and feeds these numbers straight through — no further
//    conversion.
//
//  • UFF combines unlike atoms with a *geometric* mean for both ε and σ
//    (σ_ij = √(σ_i σ_j)), which maps to LAMMPS `pair_modify mix geometric`.
//
// One representative atom type per element is used (UFF defines several types
// for some elements that differ only in bonded terms; their nonbond x1/D1 are
// identical, so the choice is immaterial for LJ). Elements beyond Lr (Z > 103)
// are not in UFF; callers fall back via `uffForElement`.

export interface UffParams {
  /** LJ well depth ε in kcal/mol (UFF D1). */
  epsilon: number;
  /** LJ σ in Å, already converted from UFF x1 via σ = x1·2^(−1/6). */
  sigma: number;
  /** UFF x1 (R_min) in Å, kept for reference / debugging. */
  rmin: number;
}

// Factor converting UFF x1 (R_min) to LJ σ.
const RMIN_TO_SIGMA = Math.pow(2, -1 / 6);

// Raw UFF nonbond data: element symbol -> [x1 (Å), D1 (kcal/mol)].
// Verbatim from Rappé 1992 Table 1 (one type per element).
const UFF_RAW: Record<string, [number, number]> = {
  H: [2.886, 0.044],
  He: [2.362, 0.056],
  Li: [2.451, 0.025],
  Be: [2.745, 0.085],
  B: [4.083, 0.18],
  C: [3.851, 0.105],
  N: [3.66, 0.069],
  O: [3.5, 0.06],
  F: [3.364, 0.05],
  Ne: [3.243, 0.042],
  Na: [2.983, 0.03],
  Mg: [3.021, 0.111],
  Al: [4.499, 0.505],
  Si: [4.295, 0.402],
  P: [4.147, 0.305],
  S: [4.035, 0.274],
  Cl: [3.947, 0.227],
  Ar: [3.868, 0.185],
  K: [3.812, 0.035],
  Ca: [3.399, 0.238],
  Sc: [3.295, 0.019],
  Ti: [3.175, 0.017],
  V: [3.144, 0.016],
  Cr: [3.023, 0.015],
  Mn: [2.961, 0.013],
  Fe: [2.912, 0.013],
  Co: [2.872, 0.014],
  Ni: [2.834, 0.015],
  Cu: [3.495, 0.005],
  Zn: [2.763, 0.124],
  Ga: [4.383, 0.415],
  Ge: [4.28, 0.379],
  As: [4.23, 0.309],
  Se: [4.205, 0.291],
  Br: [4.189, 0.251],
  Kr: [4.141, 0.22],
  Rb: [4.114, 0.04],
  Sr: [3.641, 0.235],
  Y: [3.345, 0.072],
  Zr: [3.124, 0.069],
  Nb: [3.165, 0.059],
  Mo: [3.052, 0.056],
  Tc: [2.998, 0.048],
  Ru: [2.963, 0.056],
  Rh: [2.929, 0.053],
  Pd: [2.899, 0.048],
  Ag: [3.148, 0.036],
  Cd: [2.848, 0.228],
  In: [4.463, 0.599],
  Sn: [4.392, 0.567],
  Sb: [4.42, 0.449],
  Te: [4.47, 0.398],
  I: [4.5, 0.339],
  Xe: [4.404, 0.332],
  Cs: [4.517, 0.045],
  Ba: [3.703, 0.364],
  La: [3.522, 0.017],
  Ce: [3.556, 0.013],
  Pr: [3.606, 0.01],
  Nd: [3.575, 0.01],
  Pm: [3.547, 0.009],
  Sm: [3.52, 0.008],
  Eu: [3.493, 0.008],
  Gd: [3.368, 0.009],
  Tb: [3.451, 0.007],
  Dy: [3.428, 0.007],
  Ho: [3.409, 0.007],
  Er: [3.391, 0.007],
  Tm: [3.374, 0.006],
  Yb: [3.355, 0.228],
  Lu: [3.64, 0.041],
  Hf: [3.141, 0.072],
  Ta: [3.17, 0.081],
  W: [3.069, 0.067],
  Re: [2.954, 0.066],
  Os: [3.12, 0.037],
  Ir: [2.84, 0.073],
  Pt: [2.754, 0.08],
  Au: [3.293, 0.039],
  Hg: [2.705, 0.385],
  Tl: [4.347, 0.68],
  Pb: [4.297, 0.663],
  Bi: [4.37, 0.518],
  Po: [4.709, 0.325],
  At: [4.75, 0.284],
  Rn: [4.765, 0.284],
  Fr: [4.9, 0.05],
  Ra: [3.677, 0.404],
  Ac: [3.478, 0.033],
  Th: [3.396, 0.026],
  Pa: [3.424, 0.022],
  U: [3.395, 0.022],
  Np: [3.424, 0.019],
  Pu: [3.424, 0.016],
  Am: [3.381, 0.014],
  Cm: [3.326, 0.013],
  Bk: [3.339, 0.013],
  Cf: [3.313, 0.013],
  Es: [3.299, 0.012],
  Fm: [3.286, 0.012],
  Md: [3.274, 0.011],
  No: [3.248, 0.011],
  Lr: [3.236, 0.011],
};

/** Map element symbol -> ready-to-use LJ params (ε kcal/mol, σ Å). */
export const UFF_PARAMS: Map<string, UffParams> = new Map(
  Object.entries(UFF_RAW).map(([symbol, [x1, d1]]) => [
    symbol,
    { epsilon: d1, sigma: x1 * RMIN_TO_SIGMA, rmin: x1 },
  ]),
);

// Fallback for elements outside UFF (the synthetic super-heavies Z > 103,
// Rf…Og). These have no experimental/UFF parameters; reuse a heavy-actinide-
// like value so they still produce a runnable, visually reasonable system
// rather than crashing the run. Chosen near Lr's parameters.
const SUPERHEAVY_FALLBACK: UffParams = {
  epsilon: 0.011,
  sigma: 3.236 * RMIN_TO_SIGMA,
  rmin: 3.236,
};

/**
 * Look up LJ parameters for an element symbol. Returns UFF values when known,
 * otherwise a super-heavy fallback so every element in the material database
 * (through Z=118) still yields a runnable LAMMPS system.
 */
export function uffForElement(symbol: string): UffParams {
  return UFF_PARAMS.get(symbol) ?? SUPERHEAVY_FALLBACK;
}

/** True when `symbol` has genuine UFF parameters (not the fallback). */
export function hasUffParams(symbol: string): boolean {
  return UFF_PARAMS.has(symbol);
}
